import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  KEYWORD_TYPES,
  TIME_RANGES,
  type TraceInstanceOption,
  type TraceKeywordType,
  type TraceSearch,
  withTimeRange
} from './traceSearch'

interface TraceSearchBarProps {
  value: TraceSearch
  onChange: (next: TraceSearch) => void
  /** 传入时展示实例下拉 */
  instances?: TraceInstanceOption[]
}

/** Trace 列表搜索栏 */
export function TraceSearchBar({ value, onChange, instances }: TraceSearchBarProps) {
  const [draft, setDraft] = useState(value.keyword)
  useEffect(() => setDraft(value.keyword), [value.keyword])

  const keywordType = KEYWORD_TYPES.find(t => t.value === value.keywordType) ?? KEYWORD_TYPES[0]
  const selectedInstance = instances?.find(inst => inst.id === value.instanceId)
  // 条件变化时按当前时间重新固定时间窗口
  const emit = (next: TraceSearch) => onChange(withTimeRange(next, next.hours))
  const applyKeyword = () => emit({ ...value, keyword: draft.trim() })

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-[130px]">
        <Select
          value={value.hours === null ? '' : String(value.hours)}
          onValueChange={v => emit({ ...value, hours: v ? Number(v) : null })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="全部时间">
              {TIME_RANGES.find(r => r.hours === value.hours)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">全部时间</SelectItem>
            {TIME_RANGES.map(r => (
              <SelectItem key={r.hours} value={String(r.hours)}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {instances && (
        // 可搜索的实例下拉
        <div className="w-[220px]">
          <Select
            value={value.instanceId}
            onValueChange={v => emit({ ...value, instanceId: v })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="全部实例">
                {selectedInstance ? selectedInstance.name : value.instanceId || undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent searchable searchPlaceholder="搜索实例名称或 ID...">
              <SelectItem value="" searchKeywords="全部实例">
                全部实例
              </SelectItem>
              {instances.map(inst => (
                <SelectItem key={inst.id} value={inst.id} searchKeywords={`${inst.name} ${inst.id}`}>
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="truncate">{inst.name}</span>
                    <span className="text-xs text-muted-foreground font-mono shrink-0">
                      {inst.id}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="w-[110px]">
          <Select
            value={value.keywordType}
            onValueChange={v => emit({ ...value, keywordType: v as TraceKeywordType })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue>{keywordType.label}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {KEYWORD_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          className="h-8 w-[240px] text-xs"
          value={draft}
          placeholder={keywordType.placeholder}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') applyKeyword()
          }}
        />
        <Button size="sm" className="h-8" onClick={applyKeyword}>
          搜索
        </Button>
        {(value.keyword || draft) && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => {
              setDraft('')
              emit({ ...value, keyword: '' })
            }}
          >
            清空
          </Button>
        )}
      </div>
    </div>
  )
}
